import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Medal, Crown, Star, TrendingUp, RefreshCw, Rocket, Zap } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { useTheme } from './ThemeToggle';

// Générer un pseudo anonyme à partir de l'ID
const generateAnonymousName = (customerId, customerName) => {
  const initials = customerName ? customerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'XX';
  const hash = customerId ? customerId.slice(-4).toUpperCase() : '0000';
  return `${initials}***${hash}`;
};

export default function CustomerLeaderboard({ currentCustomerId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRank, setMyRank] = useState(null);
  const { isDarkMode } = useTheme();

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await api.getCustomerLeaderboard();
      setLeaderboard(data.leaderboard || []);
      
      if (currentCustomerId) {
        const myPosition = data.leaderboard?.findIndex(c => c.id === currentCustomerId);
        if (myPosition !== -1) {
          setMyRank(myPosition + 1);
        }
      }
    } catch (error) {
      console.error('Erreur leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, [currentCustomerId]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-gray-500 font-bold text-lg font-mono">#{rank}</span>;
  };

  const getRankGlow = (rank) => {
    if (rank === 1) return '0 0 20px rgba(234, 179, 8, 0.5)';
    if (rank === 2) return '0 0 15px rgba(156, 163, 175, 0.4)';
    if (rank === 3) return '0 0 15px rgba(180, 83, 9, 0.4)';
    return 'none';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #422006 100%)',
        boxShadow: '0 0 40px rgba(234, 179, 8, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(234, 179, 8, 0.3)'
      }}
    >
      {/* Étoiles animées */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-yellow-400"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
            animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Bordure supérieure */}
      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
      
      {/* HUD Corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-yellow-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-yellow-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-yellow-500/30" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-yellow-500/30" />

      {/* Header */}
      <div className="relative p-4 flex items-center justify-between border-b border-yellow-500/20">
        <div className="flex items-center gap-3">
          <motion.div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(234, 179, 8, 0.1) 100%)',
              border: '1px solid rgba(234, 179, 8, 0.3)'
            }}
            animate={{ boxShadow: ['0 0 10px rgba(234, 179, 8, 0.2)', '0 0 20px rgba(234, 179, 8, 0.4)', '0 0 10px rgba(234, 179, 8, 0.2)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Trophy className="w-5 h-5 text-yellow-400" />
          </motion.div>
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              🏆 Hall of Fame
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                TOP 10
              </span>
            </h3>
            <p className="text-[11px] text-yellow-500/70 font-mono tracking-wider">
              CLASSEMENT GALACTIQUE • ÉLITE
            </p>
          </div>
        </div>
        
        <Button
          onClick={loadLeaderboard}
          size="sm"
          variant="ghost"
          className="text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Mon rang */}
      {myRank && (
        <motion.div 
          className="mx-4 mt-4 p-3 rounded-xl border"
          style={{
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)',
            borderColor: 'rgba(6, 182, 212, 0.5)',
            boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)'
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 font-semibold text-sm">Votre position orbitale</span>
            </div>
            <div className="flex items-center gap-2">
              <motion.span 
                className="font-bold text-2xl text-white font-mono"
                animate={{ textShadow: ['0 0 10px rgba(6, 182, 212, 0.5)', '0 0 20px rgba(6, 182, 212, 0.8)', '0 0 10px rgba(6, 182, 212, 0.5)'] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                #{myRank}
              </motion.span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Liste */}
      <div className="relative p-4 space-y-2 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <motion.div 
              className="w-10 h-10 rounded-xl bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <Trophy className="w-5 h-5 text-yellow-400" />
            </motion.div>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="py-8 text-center">
            <motion.div
              className="w-14 h-14 mx-auto mb-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Trophy className="w-7 h-7 text-yellow-400/50" />
            </motion.div>
            <p className="text-gray-400">Aucun classement disponible</p>
            <p className="text-xs text-gray-600 mt-1 font-mono">SCANNING...</p>
          </div>
        ) : (
          leaderboard.slice(0, 10).map((customer, idx) => {
            const rank = idx + 1;
            const isMe = customer.id === currentCustomerId;
            const points = (customer.completedCardsCount || 0) * 10 + (customer.referralCount || 0) * 5;
            
            return (
              <motion.div
                key={customer.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`p-3 rounded-xl border transition-all ${isMe ? 'ring-2 ring-cyan-400' : ''}`}
                style={{
                  background: rank <= 3 
                    ? `linear-gradient(135deg, rgba(234, 179, 8, ${0.2 - idx * 0.05}) 0%, rgba(0,0,0,0.3) 100%)`
                    : 'rgba(0,0,0,0.3)',
                  borderColor: rank === 1 ? 'rgba(234, 179, 8, 0.5)' : rank === 2 ? 'rgba(156, 163, 175, 0.4)' : rank === 3 ? 'rgba(180, 83, 9, 0.4)' : 'rgba(255,255,255,0.1)',
                  boxShadow: getRankGlow(rank)
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 flex items-center justify-center">
                    {getRankIcon(rank)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold truncate ${isMe ? 'text-cyan-400' : 'text-white'}`}>
                      {generateAnonymousName(customer.id, customer.name)}
                      {isMe && <span className="text-xs ml-2 text-cyan-400/70">(vous)</span>}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>🎴 {customer.completedCardsCount || 0}</span>
                      {customer.referralCount > 0 && <span>👥 {customer.referralCount}</span>}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <motion.div 
                      className="flex items-center gap-1"
                      animate={rank === 1 ? { scale: [1, 1.05, 1] } : {}}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span className="font-bold text-white font-mono">{points}</span>
                    </motion.div>
                    <p className="text-[10px] text-gray-500 font-mono">PTS</p>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-yellow-500/10 flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">🏆 HALL OF FAME v2.0</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-600">PTS = cartes×10 + filleuls×5</span>
          <div className="flex items-center gap-1">
            <motion.div className="w-1.5 h-1.5 rounded-full bg-yellow-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/50" />
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-300/30" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
